import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSEO from "@/components/feature/PageSEO";
import { categories } from "@/mocks/comics";
import { hotComics } from "@/mocks/comics";
import { siteInfo } from "@/mocks/siteInfo";

export default function CategoryList() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");

  const breadcrumbItems = [{ label: t("nav.category") }];

  const allComics = hotComics;

  const filteredComics =
    activeCategory === "all"
      ? allComics
      : allComics.filter((c) => c.category === categories.find((cat) => cat.id === activeCategory)?.name);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: siteInfo.name + " - " + t("nav.category"),
      description: "浏览所有韩漫分类，恋爱、奇幻、动作、悬疑、耽美等热门分类，超过一万部韩国漫画作品。",
      url: siteInfo.url + "/category",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: siteInfo.url },
        { "@type": "ListItem", position: 2, name: t("nav.category") },
      ],
    },
  ];

  return (
    <>
      <PageSEO
        title={siteInfo.name + " - " + t("nav.category")}
        description="浏览所有韩漫分类，恋爱、奇幻、动作、悬疑、耽美等热门分类，超过一万部韩国漫画作品，免费在线阅读。"
        keywords="韩漫分类,韩国漫画分类,恋爱漫画,奇幻漫画,动作漫画,悬疑漫画,耽美漫画,免费韩漫"
        canonicalUrl={siteInfo.url + "/category"}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
                  {t("nav.category")}
                </h1>
                <p className="mt-3 text-sm text-foreground-600 max-w-xl mx-auto">
                  超过一万部韩国漫画作品，覆盖恋爱、奇幻、动作、悬疑、耽美等 20+ 个热门分类，每日更新不间断。
                </p>
              </div>

              <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
                <button
                  onClick={() => setActiveCategory("all")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === "all"
                      ? "bg-primary-500 text-white"
                      : "bg-white text-foreground-700 border border-background-200 hover:border-primary-300"
                  }`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                      activeCategory === cat.id
                        ? "bg-primary-500 text-white"
                        : "bg-white text-foreground-700 border border-background-200 hover:border-primary-300"
                    }`}
                  >
                    <i className={cat.icon + " text-sm w-4 h-4 flex items-center justify-center"}></i>
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {filteredComics.map((comic) => (
                  <div key={comic.id} className="group cursor-pointer">
                    <div className="relative rounded-2xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={comic.imageUrl}
                        alt={comic.title}
                        title={comic.title + " - 在线漫画韩漫"}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-xs text-white leading-relaxed line-clamp-2">{comic.desc}</p>
                      </div>
                    </div>
                    <span className="inline-block px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium mb-1.5">
                      {comic.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                      {comic.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
                  </div>
                ))}
              </div>

              {filteredComics.length === 0 && (
                <div className="text-center py-16">
                  <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto"></i>
                  <p className="mt-4 text-foreground-500">该分类下暂无作品</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}